/* eslint-disable indent */

import { delay } from 'utils';
import { froze, unfroze } from 'helpers/froze';
import { ANIMATION_NEW, ANIMATION_GAME_END, ANIMATION_GAME_COMPLETION, ANIMATION_FUSION } from 'constants/animations';
import { DefineOwnModule, RootActions } from 'stores/type';
import EventsManager from 'managers/events.manager';
import * as MIX_RESULT from 'constants/mix-result';
import { State, Getters, Actions, Mutations } from './types';

const moduleState: State = {
  active: null,
  activeFinal: [],
  middleFinal: [],
  activeExhausted: [],
};

const storeModule: DefineOwnModule<
  State,
  Getters,
  Mutations,
  Actions & Pick<RootActions, 'mixOccured' | 'workspaceClear' | 'workspaceFinalRemove' | 'workspaceExhaustedRemove'>
> = {
  state: moduleState,

  getters: {
    animation: (state) => state.active,
    isAnimationActive: (state) => (name) => state.active === name,

    animationFinalElements: (state) => state.activeFinal,
    animationFinalFinished: (state) => state.middleFinal,
    animationExhaustedElement: (state) => state.activeExhausted,

    isFinalForIdActive: (state) => (id) => state.activeFinal.includes(id),
    isExhaustedForIdActive: (state) => (id) => state.activeExhausted.includes(id),
  },

  actions: {
    animationChange({ commit, dispatch }, name) {
      commit('ANIMATION_CHANGE', name);
      if (name === ANIMATION_NEW) {
        window.setTimeout(() => dispatch('animationNewStart'), 250);
      }
    },
    animationReset({ dispatch, commit }) {
      commit('ANIMATION_CHANGE', null);
      unfroze();
      dispatch('mixReset');
    },

    animationFinalStart({ commit, getters }, id) {
      if (getters.animationFinalElements.includes(id)) return;

      commit('ANIMATION_FINAL_START', id);
    },
    animationFinalMiddle({ commit }, id) {
      commit('ANIMATION_FINAL_MIDDLE', id);
    },
    animationFinalFinished({ commit }, id) {
      commit('ANIMATION_FINAL_FINISH', id);
    },

    animationExhaustedStart({ commit, getters }, id) {
      if (getters.isExhaustedForIdActive(id)) return;

      commit('ANIMATION_EXHAUSTED_START', id);
    },
    animationExhaustedFinished({ dispatch, commit }, id) {
      commit('ANIMATION_EXHAUSTED_FINISH', id);
      dispatch('libraryReload');

      window.setTimeout(() => dispatch('animationCompletionCheck'), 30);
    },

    async mixOccured({ dispatch, getters }) {
      await delay(10);
      if (getters.mixStatus === MIX_RESULT.FAIL) return;

      if (getters.mixStatus === MIX_RESULT.MIXED) {
        EventsManager.emit('ANIMATION_MIXED_START');
        return;
      }

      if (getters.mixStatus === MIX_RESULT.NEW) {
        froze();
        dispatch('animationChange', ANIMATION_FUSION);
      }
    },

    async animationFusionMiddle({ dispatch, getters }) {
      dispatch('animationNewCheck');
      if (getters.mixHasNewChildren) return;

      dispatch('workspaceHandleMixResult', { removeParents: true });
      await delay(250);
      dispatch('workspaceExhaustedCheck');
    },

    animationNewCheck({ dispatch, getters }) {
      if (!getters.mixHasNewChildren) return;

      dispatch('animationChange', ANIMATION_NEW);
      EventsManager.emit('ANIMATION_NEW_POP');
    },
    animationNewStart({ dispatch }) {
      dispatch('workspaceRemoveMixParents');
    },
    async animationNewEnding({ dispatch }, { removeParents = true } = {}) {
      dispatch('workspaceHandleMixResult', { removeParents });
      // on new animation ending change all exhausted WorkspaceBox to FinalBox
      delay(250);
      dispatch('workspaceExhaustedCheck');
    },
    animationNewEnd({ dispatch }) {
      dispatch('animationEndCheck');
    },

    animationEndCheck({ dispatch, getters }) {
      if (!getters.hasAllElements) return;

      dispatch('animationChange', ANIMATION_GAME_END);
      EventsManager.emit('GAME_ENDED');
    },

    // TODO: refactor - move to store watch
    async workspaceClear({ dispatch }) {
      await delay(1000);
      dispatch('animationCompletionCheck');
    },
    workspaceFinalRemove({ dispatch }) {
      dispatch('animationCompletionCheck');
    },
    async workspaceExhaustedRemove({ dispatch }) {
      await delay(10);
      dispatch('animationCompletionCheck');
    },
    animationCompletionCheck({ getters, dispatch }) {
      if (!getters.hasAllCleared) return;
      if (!getters.needsGameCompletionAnimation) return;

      dispatch('animationChange', ANIMATION_GAME_COMPLETION);
      EventsManager.emit('GAME_COMPLETED');
    },
    animationCompletionEnding({ commit, dispatch }) {
      commit('WORKSPACE_INACTIVE_RESET');
      dispatch('libraryReload');
    },
  },

  mutations: {
    ANIMATION_CHANGE(state, name) {
      state.active = name;
    },

    ANIMATION_FINAL_START(state, id) {
      state.activeFinal.push(id);
    },
    ANIMATION_FINAL_MIDDLE(state, id) {
      state.middleFinal.push(id);
    },
    ANIMATION_FINAL_FINISH(state, elementId) {
      state.activeFinal = state.activeFinal.filter((id) => id !== elementId);
      state.middleFinal = state.middleFinal.filter((id) => id !== elementId);
    },

    ANIMATION_EXHAUSTED_START(state, id) {
      state.activeExhausted.push(id);
    },
    ANIMATION_EXHAUSTED_FINISH(state, elementId) {
      state.activeExhausted = state.activeExhausted.filter((id) => id !== elementId);
    },
  },
};

export default storeModule;
